import {floatPriceFromFormatted} from '@optimaxdev/utils';

import {ENDPOINTS} from 'constants/endpoints';
import {CartItemType} from 'desktop/pages/cart/types';
import {
    OrderReviewTotalsItemsType,
    OrderReviewTotalsType,
} from 'desktop/types/orderReviewTotalsType';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';
import {PayPalAddressType, PayPalOrderDataType} from 'types/paypalType';
import {ShippingMethodType} from 'types/shippingMethodsType';

type CreateOrderLinesArgumentsType = {
    items: CartItemType[] | OrderReviewTotalsItemsType[];
    copay: string | number;
    shippingProtectionPrice?: number | null;
};

/**
 * Contains duplicated logic which creates order lines for paypal in the cart & checkout
 */
const createOrderLines = ({
    copay,
    items,
    shippingProtectionPrice,
}: CreateOrderLinesArgumentsType): PayPalOrderDataType['payment']['orderLines'] => {
    const orderLines: PayPalOrderDataType['payment']['orderLines'] = items.map(
        ({name, qty, total}) => ({
            name,
            quantity: Number(qty),
            unitPrice: Number(
                (
                    (floatPriceFromFormatted(total) * 100) /
                    (floatPriceFromFormatted(qty) * 100)
                ).toFixed(2),
            ),
        }),
    );

    if (shippingProtectionPrice) {
        orderLines.push({
            name: 'Shipping Protection',
            quantity: 1,
            unitPrice: Number(shippingProtectionPrice),
        });
    }

    if (Number(copay))
        // For UHC users we should create the additional item - co-pay and add it to subtotal
        // b/c subtotal should be equal to the sum of items costs
        orderLines.push({name: 'Co-pay', quantity: 1, unitPrice: Number(copay)});

    return orderLines;
};

/**
 * Get paypal order data (items, amount, tax amount) from totals endpoint
 *
 * @param {ApplicationStoreType} store - current store
 */
export const getPayPalOrderDataFromTotals = ({
    shippingAddress,
    shippingMethodsList,
    shippingProtectionPrice,
    totals,
}: {
    totals: OrderReviewTotalsType;
    shippingAddress: PayPalAddressType | null;
    shippingProtectionPrice?: number | null;
    shippingMethodsList?: ShippingMethodType[] | null;
}): PayPalOrderDataType => {
    const orderLines = createOrderLines({
        copay: totals.copay,
        items: totals.items,
        shippingProtectionPrice,
    });
    return {
        payment: {
            amount: totals.grandTotal,
            discount: totals.discount.price,
            orderLines,
            shipping: totals.shipping.price,
            shippingAddress,
            taxTotal: totals.totalTax,
            ...(shippingMethodsList && {shippingMethods: shippingMethodsList}),
        },
    };
};

/**
 * Extracts token from backend request
 * @param {string} approveUrl - https://www.paypal.com/checkoutnow?token=78E910950X4861440
 */
const extractPayPalToken = (approveUrl: string) => {
    let token;
    try {
        token = new URL(approveUrl).searchParams.get('token');
    } catch (e) {
        logWithSentry(e, 'Paypal token is not valid, order creation failed on backend');
    }
    return token;
};

/**
 * getTokenPayPal
 */
export const getTokenPayPal = async ({
    isEnabledShippingProtection,
    price,
    selectedShippingMethodCode,
    shippingAddress,
    shippingMethodsList,
    totals,
}: {
    totals: OrderReviewTotalsType;
    shippingAddress: PayPalAddressType;
    isEnabledShippingProtection: boolean;
    price?: number;
    shippingMethodsList: ShippingMethodType[];
    selectedShippingMethodCode: string;
}) => {
    const shippingMethodListData = shippingMethodsList.map(item => ({
        ...item,
        selected: item.code === selectedShippingMethodCode,
    }));

    const {data} = await apiConfig.instanceGusaBackend({
        baseURL: apiConfig.API_URL,
        data: isEnabledShippingProtection
            ? getPayPalOrderDataFromTotals({
                  shippingAddress,
                  shippingMethodsList: shippingMethodListData,
                  shippingProtectionPrice: price,
                  totals,
              })
            : getPayPalOrderDataFromTotals({
                  shippingAddress,
                  shippingMethodsList: shippingMethodListData,
                  totals,
              }),
        method: 'post',
        url: ENDPOINTS.PAYPAL_TOKEN,
    });
    return {id: data.id, token: extractPayPalToken(data.approveUrl)};
};
